import { useBaseStyles } from './useBaseStyles'
import { styled } from 'goober'
import { useTheme } from '../useTheme'
import { useBreakpoints } from '../useBreakpoints'

import { PopoverContent } from '../../components/Popover'

export const useInAppNotificationStyles = () => {
  const { P, FlexBox } = useBaseStyles()
  const theme = useTheme()
  const { smUp } = useBreakpoints()

  const isDarkMode = theme.palette.mode === 'dark'

  const documentTypeColorCoding = {
    pdf: {
      iconColor: isDarkMode ? 'rgba(172, 82, 82, 1)' : 'rgba(212, 121, 121, 1)',
      background: isDarkMode
        ? 'rgba(172, 82, 82, 0.2)'
        : 'rgba(218, 118, 118, 0.15)',
      title: 'PDF'
    },
    xlsx: {
      iconColor: isDarkMode ? 'rgba(51, 143, 104, 1)' : 'rgba(85 ,149 ,121,1)',
      background: isDarkMode
        ? 'rgba(51, 143, 104, 0.2)'
        : 'rgba(69, 125, 101, 0.15)',
      title: 'XLSX'
    },
    xls: {
      iconColor: isDarkMode ? 'rgba(51, 143, 104, 1)' : 'rgba(85, 149, 121, 1)',
      background: isDarkMode
        ? 'rgba(51, 143, 104, 0.2)'
        : 'rgba(69, 125, 101, 0.15)',
      title: 'XLS'
    },
    docx: {
      iconColor: isDarkMode
        ? 'rgba(106, 159, 205, 1)'
        : 'rgba(101, 162, 219, 1)',
      background: isDarkMode
        ? 'rgba(106, 159, 205, 0.2)'
        : 'rgba(123, 175, 225, 0.15)',
      title: 'DOCX'
    },
    doc: {
      iconColor: isDarkMode
        ? 'rgba(106, 159, 205, 1)'
        : 'rgba(101, 162, 219, 1)',
      background: isDarkMode
        ? 'rgba(106, 159, 205, 0.2)'
        : 'rgba(123, 175, 225, 0.15)',
      title: 'DOC'
    },
    txt: {
      iconColor: isDarkMode ? 'rgba(185, 168, 75, 1)' : 'rgba(201, 184, 91, 1)',
      background: isDarkMode
        ? 'rgba(185, 168, 75, 0.2)'
        : 'rgba(206, 192, 116, 0.15)',
      title: 'TXT'
    }
  }

  const commonStyles = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    background: 'transparent',
    borderRadius: '50%',
    cursor: 'pointer',
    transition: 'background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    marginRight: '0.25rem'
  }

  const threeDotsButtonStyle = {
    ...commonStyles,
    color: theme.palette.text.secondary,
    padding: '7px'
  }

  const filterButtonStyle = {
    ...commonStyles,
    color: theme.palette.text.secondary,
    padding: '7px',
    marginRight: '0.5rem'
  }

  return {
    threeDotsButtonStyle,
    filterButtonStyle,
    DocsComponentContainer: styled('div')(({ docType = 'txt' }) => {
      const docConfig =
        documentTypeColorCoding[docType.toLowerCase()] ||
        documentTypeColorCoding.txt
      return {
        width: '50px',
        height: '50px',
        borderRadius: '5px',
        background: docConfig.background,
        color: docConfig.iconColor,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center'
      }
    }),

    MainContainer: styled('div')({
      height: smUp ? '60vh' : '80vh',
      minHeight: smUp ? '60vh' : '80vh',
      maxHeight: smUp ? '60vh' : '80vh',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }),

    NotificationContentContainer: styled('div')({
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      color: theme.palette.text.primary
    }),

    NotificationContentTypography: styled(P)({
      fontSize: '14px',
      fontWeight: 'bold',
      wordWrap: 'break-word',
      paddingRight: '5px'
    }),

    NotificationContentActionButtonsContainer: styled(FlexBox)({ gap: '10px' }),

    NotificationTimeTypography: styled(P)(({ logo }) => ({
      fontSize: '0.7rem',
      color: theme.palette.text.primary,
      paddingLeft: logo ? '2.5rem' : '0.8rem'
    })),

    NotificationActionsStyledThreeDots: styled('button')({
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '7px',
      border: 'none',
      background: 'transparent',
      borderRadius: '50%',
      cursor: 'pointer',
      transition: 'background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
      color: theme.palette.text.secondary
    }),

    StyledPopoverContent: styled(PopoverContent)({
      backgroundColor: theme.palette.background.paper,
      borderRadius: '8px',
      padding: '8px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      zIndex: 10000
    }),

    StyledMenuItem: styled('button')(({ hoveredItem, type }) => ({
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 12px',
      border: 'none',
      backgroundColor:
        hoveredItem === type
          ? theme.palette.action?.hover || 'rgba(0, 0, 0, 0.04)'
          : 'transparent',
      cursor: 'pointer',
      borderRadius: '4px',
      transition: 'background-color 0.2s',
      fontSize: '14px',
      fontFamily: theme.typography.fontFamily,
      width: '100%',
      textAlign: 'left',
      color:
        type === 'delete'
          ? theme.palette.error.main
          : theme.palette.text?.primary || '#000'
    })),

    InAppMessageContainer: styled('section')(({ read }) => ({
      padding: '1rem 1rem 0.6rem 1rem',
      background: read ? 'transparent' : theme.palette.primary.main + '1A',
      cursor: 'pointer',
      '&:hover': { translate: '0 -2px' },
      transition: '0.3s translate ease-in-out',
      borderBottom: `1px solid ${theme.palette.divider}`,
      width: '100%',
      boxSizing: 'border-box'
    })),

    InAppMessageFooterContainer: styled(FlexBox)({
      justifyContent: 'space-between'
    }),

    ReadMoreTypography: styled('p')({
      fontSize: '12px',
      fontWeight: 300,
      marginTop: '1px',
      marginBottom: '8px'
    }),

    StyledReadMoreButton: styled('button')({
      all: 'unset',
      fontSize: '12px',
      textDecoration: 'none',
      color: theme.palette.primary.main,
      cursor: 'pointer',
      display: 'inline-block',
      marginTop: '-1px',
      marginBottom: '5px'
    })
  }
}
